/**
 * Why an AI draft is priced, or why it is not — turned into something a
 * dispatcher can act on.
 *
 * WHY THIS EXISTS
 * ---------------
 * AI Suggest resolves ONE rate sheet or none. It takes the most specific tier
 * with candidates — the customer's own bid contracts, else the company-wide
 * books — and uses it only when that tier holds exactly one sheet. Otherwise it
 * refuses, and the draft comes back unpriced.
 *
 * The refusal is correct. TNT's company-wide books are two BILLING MODELS, not
 * two versions: Casing Division prices a run turnkey, Completions prices the
 * same work a la carte. A blended draft that LOOKS priced gets approved and
 * invoices a customer under a structure they never agreed to.
 *
 * What was broken is that the refusal was SILENT. The API prompt requires
 * `NEVER skip a service`, so the draft still returns a full set of line items
 * with every rate blank — and this screen reported that as a green
 * "AI generated 6 line items" toast. Measured on production 2026-08-23, that is
 * 7 of TNT's 15 live jobs: every Lonquist, Keyera, SubTerra and Cenovus job,
 * unpriced, while 287 priced items sat reachable behind the ambiguity. TNT
 * read it as "AI Suggest only works for customers who have their own rate
 * sheet" — the right observation, the wrong cause.
 *
 * THE TWO UNPRICED REASONS WANT OPPOSITE ACTIONS, which is the whole point of
 * not collapsing them into a boolean:
 *
 *   ambiguous  → you have several price books and nothing can choose. PICK ONE.
 *   no_sheets  → nothing could price this at all. CREATE a rate sheet.
 *
 * A single "unpriced" message would send a TNT dispatcher off to build a price
 * book they already have three of.
 *
 * A LINE WITH NO RATE IS NOT EVIDENCE ON ITS OWN, so this reads the reason
 * rather than inferring it from the rates. A resolved sheet legitimately misses
 * services — that is exactly why the API emits `rate: null` instead of skipping
 * the line — and counting blanks would mislabel that as a resolution failure.
 * Same principle as "absent renders as an em dash, never 0": do not derive a
 * fact from the shape of a gap when the fact is available.
 */

export type RateSheetResolutionReason =
  | 'resolved'
  | 'no_sheets'
  | 'ambiguous'
  /**
   * The job names a sheet that is no longer usable — expired, deactivated, or
   * belonging to a different customer's contract. The API deliberately does NOT
   * fall through to the next tier here: somebody chose a sheet for this job and
   * it has since broken, and quietly pricing off a different book would discard
   * that decision silently. Same remedy as `ambiguous`, different explanation.
   */
  | 'stale_override';

export type RateSheetTier = 'job' | 'customer' | 'customer_default' | 'company';

export interface RateSheetCandidateSummary {
  id: string;
  name: string;
  rateType?: string | null;
}

export interface RateSheetResolution {
  sheet: { id: string; name: string } | null;
  reason: RateSheetResolutionReason;
  tier: RateSheetTier | null;
  candidates: RateSheetCandidateSummary[];
}

export interface AiDraftNotice {
  /** `warning` is amber and persists; `success` is green and auto-dismisses. */
  tone: 'success' | 'warning';
  /** The toast line. */
  text: string;
  /**
   * The sheets a human has to choose between, for a banner that does NOT
   * auto-dismiss. A toast that vanishes in five seconds cannot carry a task,
   * and the names are the actionable part — "3 price books matched" tells a
   * dispatcher there is a decision; the names tell them what the decision is.
   */
  candidates: RateSheetCandidateSummary[];
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Read the resolution defensively.
 *
 * An older API build sends no `rateSheetResolution` at all. Returning null for
 * that case — rather than guessing `no_sheets` — matters during the deploy gap:
 * guessing would tell every dispatcher on every tenant that pricing had failed,
 * for the minutes between the frontend shipping and the API catching up. An
 * absent field means "this build cannot tell me", which is not the same as
 * "there was no sheet", and the two must not render the same.
 */
export function readRateSheetResolution(draft: unknown): RateSheetResolution | null {
  if (!draft || typeof draft !== 'object') return null;
  const r = (draft as { rateSheetResolution?: unknown }).rateSheetResolution;
  if (!r || typeof r !== 'object') return null;
  const reason = (r as { reason?: unknown }).reason;
  if (
    reason !== 'resolved' &&
    reason !== 'no_sheets' &&
    reason !== 'ambiguous' &&
    reason !== 'stale_override'
  ) return null;
  const raw = (r as { candidates?: unknown }).candidates;
  return {
    sheet: ((r as { sheet?: RateSheetResolution['sheet'] }).sheet) ?? null,
    reason,
    tier: ((r as { tier?: RateSheetResolution['tier'] }).tier) ?? null,
    candidates: Array.isArray(raw) ? (raw as RateSheetCandidateSummary[]) : [],
  };
}

/**
 * The message for a finished AI draft.
 *
 * `lineItemCount` and `confidence` keep the information the old toast carried;
 * the resolution decides the TONE, because that is the part a dispatcher acts
 * on. A draft with six unpriced line items is not a success, however many line
 * items it has.
 */
export function describeAiDraft(
  lineItemCount: number,
  confidence: string | null | undefined,
  resolution: RateSheetResolution | null
): AiDraftNotice {
  const items = `${lineItemCount} ${plural(lineItemCount, 'line item', 'line items')}`;
  const conf = confidence ? ` (${confidence} confidence)` : '';

  if (resolution?.reason === 'stale_override') {
    return {
      tone: 'warning',
      // Named differently from `ambiguous` on purpose. "Pick one" alone would
      // read as though nobody had chosen yet, when in fact someone did and the
      // sheet has since gone — which is what the reader needs to know before
      // picking again.
      text: `The rate sheet chosen for this job is no longer available — pick another. `
        + `AI generated ${items} with no pricing.`,
      candidates: resolution.candidates,
    };
  }

  if (resolution?.reason === 'ambiguous') {
    const n = resolution.candidates.length;
    const what = resolution.tier === 'customer'
      ? plural(n, 'rate sheet', 'rate sheets')
      : plural(n, 'price book', 'price books');
    return {
      tone: 'warning',
      // The count first, because it is the fact that makes the ask make sense:
      // "pick one" is baffling until you know there were several.
      text: `${n} ${what} matched — pick one. AI generated ${items} with no pricing.`,
      candidates: resolution.candidates,
    };
  }

  if (resolution?.reason === 'no_sheets') {
    return {
      tone: 'warning',
      text: `No rate sheet covers this customer — AI generated ${items} with no pricing.`,
      candidates: [],
    };
  }

  // 'resolved', or an API build that does not report a resolution. Both keep
  // the message the screen has always shown.
  return { tone: 'success', text: `AI generated ${items}${conf}`, candidates: [] };
}

// ─── Choosing a sheet on the job form ────────────────────────────────────────

export interface RateSheetChoice {
  id: string;
  name: string;
  rateType?: string | null;
  customerId?: string | null;
  customer_id?: string | null;
}

/** The sheet's owner, tolerating both casings the API surfaces use. */
const ownerOf = (s: RateSheetChoice): string | null =>
  (s.customerId ?? s.customer_id) ?? null;

/**
 * The sheets that may legitimately price this customer's work: their own
 * contracts PLUS the company-wide books.
 *
 * WIDENS, it does not narrow — the same rule the API's resolver uses. A
 * customer with a bid contract can still be billed off a book, which is why
 * `GET /rate-sheets/items` was changed to stop filtering by
 * `default_rate_sheet_id`: doing so once hid all 219 of TNT's book-rate items
 * behind a filter that looked like a refinement.
 */
export function rateSheetsForCustomer(
  sheets: RateSheetChoice[],
  customerId: string | null | undefined
): { contracts: RateSheetChoice[]; books: RateSheetChoice[] } {
  const contracts = customerId
    ? sheets.filter((s) => ownerOf(s) === customerId)
    : [];
  const books = sheets.filter((s) => ownerOf(s) === null);
  return { contracts, books };
}

/**
 * The label for the "not chosen" option on the job form.
 *
 * DELIBERATELY DOES NOT PREDICT which sheet will win. That would be a second
 * implementation of the resolver's precedence chain living in the browser, and
 * CLAUDE.md's rule against client-side recomputation exists because two answers
 * to one question is worse than one answer late — nobody would know which was
 * right six months on.
 *
 * Naming the customer's stored default is NOT a prediction: it is displaying a
 * value the customer record holds. Where there is no default, this says so and
 * stops, rather than guessing at the book tier.
 */
export function inheritedRateSheetLabel(
  sheets: RateSheetChoice[],
  customerDefaultRateSheetId: string | null | undefined
): string {
  if (!customerDefaultRateSheetId) {
    return 'Not set — resolved when a ticket is drafted';
  }
  const named = sheets.find((s) => s.id === customerDefaultRateSheetId);
  return named
    ? `Not set — client default: ${named.name}`
    : 'Not set — resolved when a ticket is drafted';
}
